import { useMemo } from 'react';
import { Workspace } from '@robocorp/robocloud-internal-cds-sdk';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { RenderMode } from '~/types/common';
import { useParams } from '~/hooks';
import { organizationService } from '../../services/organizationAPI';
import { workspaceService as userWorkspaceService } from '../../services/userAPI';
import { workspaceService } from '../../services/workspaceAPI';
import {
  ApiQueryFnData,
  formatErrorPayload,
  UseApiMutationOptions,
  UseApiQueryOptions,
  UseSafeApiQueryResult,
  validateApiResponse,
} from '../utils';

export enum WorkspacesQueryErrors {
  GET_WORKSPACES_ERROR = 'GET_WORKSPACES_ERROR',
  CREATE_WORKSPACE_ERROR = 'CREATE_WORKSPACE_ERROR',
  EDIT_WORKSPACE_ERROR = 'EDIT_WORKSPACE_ERROR',
  DELETE_WORKSPACE_DATA_ERROR = 'DELETE_WORKSPACE_DATA_ERROR',
  DELETE_WORKSPACE_ERROR = 'DELETE_WORKSPACE_ERROR',
  GET_WORKSPACE_BY_SHORT_NAME_ERROR = 'GET_WORKSPACE_BY_SHORT_NAME_ERROR',
}

export const getWorkspacesQueryKey = () => ['workspaces'];
export const getWorkspacesByShortNameQueryKey = () => ['workspaceByShortName'];
export const getWorkspaceByShortNameQueryKey = (args: {
  organizationShortName: string;
  workspaceShortName: string;
}) => [...getWorkspacesByShortNameQueryKey(), args];

export function useWorkspacesQuery(
  options?: UseApiQueryOptions<typeof userWorkspaceService.getAll>,
) {
  const dispatch = useDispatch();
  return useQuery(
    getWorkspacesQueryKey(),
    async () => {
      const response = validateApiResponse(await userWorkspaceService.getAll());
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspacesQueryErrors.GET_WORKSPACES_ERROR,
          }),
        );
      },
      ...options,
    },
  );
}

export function useWorkspaceByShortNameQuery(
  {
    organizationShortName,
    workspaceShortName,
  }: { organizationShortName: string; workspaceShortName: string },
  options?: UseApiQueryOptions<typeof userWorkspaceService.getOneByShortName>,
) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useQuery(
    getWorkspaceByShortNameQueryKey({ organizationShortName, workspaceShortName }),
    async () =>
      validateApiResponse(
        await userWorkspaceService.getOneByShortName(organizationShortName, workspaceShortName),
      ).data,

    {
      onSuccess: (data) => {
        // set initial workspace in workspace list
        queryClient.setQueryData(getWorkspacesQueryKey(), (items: unknown[] = []) =>
          items.length === 0 ? [data] : items,
        );
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
}

export function useOrganizationWorkspaces(
  organizationId: string,
): UseQueryResult<Workspace[] | undefined> {
  const query = useWorkspacesQuery({
    select: (data = []) =>
      data.filter(
        ({ organization_id: workspaceOrganizationId }) =>
          workspaceOrganizationId === organizationId,
      ),
  });
  return query;
}

export function useWorkspacesByOrganizationShortName({
  organizationShortName,
}: {
  organizationShortName: string;
}): UseQueryResult<Workspace[] | undefined> {
  const query = useWorkspacesQuery({
    select: (data = []) =>
      data.filter(({ org_short_name: shortName }) => shortName === organizationShortName),
  });
  return query;
}

/**
 * Pass in 'safe' type so that the returned `data` is always defined.
 * In context of organizations/workspaces we're rendering the loader until the data is fetched.
 * So, allow the user to explicitly tell the compiler that the data will be defined.
 */
export const useCurrentWorkspace = <Mode extends RenderMode>(
  options?: UseApiQueryOptions<typeof userWorkspaceService.getOneByShortName>,
) => {
  const { organizationShortName, workspaceShortName } = useParams<
    'organizationShortName' | 'workspaceShortName'
  >();

  return useWorkspaceByShortNameQuery(
    { organizationShortName, workspaceShortName },
    options,
  ) as UseSafeApiQueryResult<Mode, typeof userWorkspaceService.getOneByShortName>;
};

/**
 * Should not be used in most cases!
 * Used for cases where workspace data is shown outside workspace routes, like:
 * - side navigation data while being on user settings or organization settings or dashboard
 */
export const useCurrentOrFirstWorkspace = <Mode extends RenderMode>(
  options?: UseApiQueryOptions<typeof userWorkspaceService.getAll>,
) => {
  const { data = [], ...query } = useWorkspacesQuery(options);
  const { organizationShortName, workspaceShortName } = useParams();

  const workspace = useMemo(() => {
    const selectedOrganizationShortName =
      organizationShortName || window.localStorage.getItem('currentOrganizationShortName');
    const selectedWorkspaceShortName =
      workspaceShortName || window.localStorage.getItem('currentWorkspaceShortName');

    if (!selectedOrganizationShortName && !selectedWorkspaceShortName) return data[0];
    const workspaceMatch = selectedWorkspaceShortName
      ? data.find(({ short_name: shortName }) => shortName === selectedWorkspaceShortName)
      : undefined;

    if (!workspaceMatch || workspaceMatch.org_short_name !== selectedOrganizationShortName) {
      const organizationMatch = selectedOrganizationShortName
        ? data.find(
            ({ org_short_name: organizationShortName }) =>
              organizationShortName === selectedOrganizationShortName,
          )
        : undefined;
      if (organizationMatch) return organizationMatch;
    }

    return workspaceMatch ?? data[0];
  }, [data, organizationShortName, workspaceShortName]);

  const queryWithCurrentWorkspace = query as UseQueryResult<Workspace | undefined>;
  queryWithCurrentWorkspace.data = workspace;
  return queryWithCurrentWorkspace as UseSafeApiQueryResult<
    Mode,
    typeof userWorkspaceService.getOneByShortName
  >;
};

export function useCreateWorkspaceMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ organizationId, name }: { organizationId: string; name: string }) => {
      const response = validateApiResponse(
        await organizationService.insertWorkspace(organizationId, name),
      );
      return response.data;
    },
    {
      onSuccess: (newWorkspace) => {
        queryClient.invalidateQueries(getWorkspacesQueryKey());
        const data = queryClient.getQueryState<Workspace[]>(getWorkspacesQueryKey())?.data;
        if (!newWorkspace) return;
        const nextState = [...(data || []), newWorkspace];
        queryClient.setQueryData(getWorkspacesQueryKey(), nextState);
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspacesQueryErrors.CREATE_WORKSPACE_ERROR,
          }),
        );
      },
    },
  );
}
export const usePatchWorkspaceMutation = <T extends typeof workspaceService.editOneById>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      organizationShortName,
      workspaceShortName,
      data,
    }: {
      organizationShortName: string;
      workspaceShortName: string;
      data: Partial<ApiQueryFnData<T>>;
    }) => {
      const workspaceData: ApiQueryFnData<T> | undefined = queryClient.getQueryData(
        getWorkspaceByShortNameQueryKey({ organizationShortName, workspaceShortName }),
      );

      if (!workspaceData) {
        throw new Error('failed to find workspace to patch');
      }

      return validateApiResponse(
        await workspaceService.editOneById(workspaceData.id, {
          ...workspaceData,
          ...data,
          details: {
            ...workspaceData.details,
            ...data.details,
          },
        }),
      ).data;
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: (updatedData, { organizationShortName, workspaceShortName }) => {
        queryClient.invalidateQueries(
          getWorkspaceByShortNameQueryKey({ organizationShortName, workspaceShortName }),
        );
        const data = queryClient.getQueryState<Workspace[]>(getWorkspacesQueryKey())?.data;
        if (!updatedData || !data) return;

        const updatedIdx = data?.findIndex(({ id }) => id === updatedData?.id);
        if (updatedIdx === -1) return;

        queryClient.setQueryData(getWorkspacesQueryKey(), [
          ...data.slice(0, updatedIdx),
          { ...data[updatedIdx], ...updatedData },
          ...data.slice(updatedIdx + 1),
        ]);
      },
      ...options,
    },
  );
};

export function useEditWorkspaceMutation(workspace: Workspace) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async (update: Partial<Pick<Workspace, 'name' | 'short_name'>>) => {
      const response = validateApiResponse(
        await workspaceService.editOneById(workspace.id, {
          ...workspace,
          ...update,
        }),
      );
      return response.data;
    },
    {
      onSuccess: (updatedData) => {
        const data = queryClient.getQueryState<Workspace[]>(getWorkspacesQueryKey())?.data;
        if (!updatedData || !data) return;

        const updatedIdx = data?.findIndex(({ id }) => id === updatedData?.id);
        if (updatedIdx === -1) return;

        queryClient.setQueryData(getWorkspacesQueryKey(), [
          ...data.slice(0, updatedIdx),
          { ...data[updatedIdx], ...updatedData },
          ...data.slice(updatedIdx + 1),
        ]);
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspacesQueryErrors.EDIT_WORKSPACE_ERROR,
          }),
        );
      },
    },
  );
}

export function useDeleteWorkspaceMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ workspaceId }: { workspaceId: string }) => {
      const response = validateApiResponse(await workspaceService.deleteOneById(workspaceId));
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getWorkspacesQueryKey());
        queryClient.invalidateQueries(getWorkspacesByShortNameQueryKey());
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspacesQueryErrors.DELETE_WORKSPACE_ERROR,
          }),
        );
      },
    },
  );
}

export function useDeleteWorkspaceDataMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ workspaceId }: { workspaceId: string }) => {
      const response = validateApiResponse(await workspaceService.deleteWorkspaceData(workspaceId));
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getWorkspacesQueryKey());
        queryClient.invalidateQueries(getWorkspacesByShortNameQueryKey());
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspacesQueryErrors.DELETE_WORKSPACE_DATA_ERROR,
          }),
        );
      },
    },
  );
}
